
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { OwnerDashboardHeader } from "@/components/nursing-homes/owner/OwnerDashboardHeader";
import { ClaimedNursingHomes } from "@/components/nursing-homes/owner/ClaimedNursingHomes";
import { PendingClaims } from "@/components/nursing-homes/owner/PendingClaims";
import { RejectedClaims } from "@/components/nursing-homes/owner/RejectedClaims";
import { NeedsInfoClaims } from "@/components/nursing-homes/owner/NeedsInfoClaims";
import { isNeedsInfoClaimStatus, isPendingClaimStatus, isRejectedClaimStatus } from "@/lib/claim-status";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Building2, Search } from "lucide-react";

export default function OwnerDashboardPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [claimedHomes, setClaimedHomes] = useState<any[]>([]);
  const [pendingClaims, setPendingClaims] = useState<any[]>([]);
  const [rejectedClaims, setRejectedClaims] = useState<any[]>([]);
  const [needsInfoClaims, setNeedsInfoClaims] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOwnerData = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const { data: allClaims, error: claimsError } = await supabase
        .from("nursing_home_claims")
        .select(
          `
            id,
            nursing_home_id,
            status,
            created_at,
            updated_at,
            notes,
            verification_documents
          `,
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (claimsError) throw claimsError;

      if (allClaims && allClaims.length > 0) {
        const approvedClaims = allClaims.filter((claim) => claim.status === "approved");
        const rejectedClaimsData = allClaims.filter((claim) => isRejectedClaimStatus(claim.status));
        const needsInfoClaimsData = allClaims.filter((claim) => isNeedsInfoClaimStatus(claim.status));
        const pendingClaimsData = allClaims.filter((claim) => isPendingClaimStatus(claim.status));

        const allNursingHomeIds = allClaims.map((claim) => claim.nursing_home_id);

        const { data: homes, error: homesError } = await supabase
          .from("nursing_homes")
          .select("*")
          .in("nh_slug", allNursingHomeIds);

        if (homesError) throw homesError;

        const approvedHomes = approvedClaims
          .map((claim) => homes?.find((home) => home.nh_slug === claim.nursing_home_id))
          .filter(Boolean);
        setClaimedHomes(approvedHomes || []);

        const pendingClaimsWithHomes = pendingClaimsData.map((claim) => ({
          ...claim,
          nursing_home: homes?.find((home) => home.nh_slug === claim.nursing_home_id),
        }));
        const rejectedClaimsWithHomes = rejectedClaimsData.map((claim) => ({
          ...claim,
          nursing_home: homes?.find((home) => home.nh_slug === claim.nursing_home_id),
        }));
        const needsInfoClaimsWithHomes = needsInfoClaimsData.map((claim) => ({
          ...claim,
          nursing_home: homes?.find((home) => home.nh_slug === claim.nursing_home_id),
        }));
        setPendingClaims(pendingClaimsWithHomes);
        setRejectedClaims(rejectedClaimsWithHomes);
        setNeedsInfoClaims(needsInfoClaimsWithHomes);
      } else {
        setClaimedHomes([]);
        setPendingClaims([]);
        setRejectedClaims([]);
        setNeedsInfoClaims([]);
      }
    } catch (err) {
      console.error("Error fetching owner data:", err);
      setError(err instanceof Error ? err : new Error("Unknown error"));
      toast.error("Failed to load your nursing homes");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && !authLoading) {
      fetchOwnerData();
    }
  }, [user, authLoading, fetchOwnerData]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to="/auth/login?redirectTo=/owner/dashboard" />;
  }

  const noClaimActivity =
    !isLoading &&
    claimedHomes.length === 0 &&
    pendingClaims.length === 0 &&
    needsInfoClaims.length === 0 &&
    rejectedClaims.length === 0;

  return (
    <Layout>
      <Helmet>
        <title>Owner Dashboard | Grandpal</title>
      </Helmet>

      <div className="container py-4 md:py-8 max-w-7xl">
        <OwnerDashboardHeader user={user} />

        {noClaimActivity && (
          <Card className="mt-4 border-grandpal-green-dark/25 bg-gradient-to-br from-grandpal-green-light/20 to-background">
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-grandpal-green-dark/10">
                  <Search className="h-6 w-6 text-grandpal-green-dark" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-grandpal-navy">Start by claiming your facility</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Use the owner claim search to find your home by name or county—no need to hunt for a small button on
                    the public directory. You can add more homes later from the same place.
                  </p>
                </div>
              </div>
              <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover shrink-0">
                <Link to="/owner/claim">
                  <Building2 className="h-4 w-4 mr-2" />
                  Find & claim
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
        
        <div className="grid grid-cols-1 gap-4 md:gap-6 mt-4">
          <NeedsInfoClaims
            claims={needsInfoClaims}
            isLoading={isLoading}
            onClaimsUpdated={fetchOwnerData}
          />
          <PendingClaims 
            claims={pendingClaims}
            isLoading={isLoading}
          />
          <RejectedClaims claims={rejectedClaims} isLoading={isLoading} />
          <ClaimedNursingHomes 
            homes={claimedHomes} 
            isLoading={isLoading}
            error={error}
          />
        </div>
      </div>
    </Layout>
  );
}
